import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, Injector, linkedSignal, signal, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonButtonRowWidget, CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonSelectFieldWidget, Option } from '@eforall/common';
import { DashboardDialogFramePart, DialogContext } from '@eforall/dashboard';
import { AccVentureDropdownItem } from 'interfaces';
import { FuncService } from '../../../../../../services';

export interface AddVentureDialogContextData {
	accVentures: AccVentureDropdownItem[],
	accTeamId: number,
}

@Component({
	selector: 'add-venture-to-team-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		CommonButtonRowWidget,
		CommonSelectFieldWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './add-venture.dialog.html'
})

export class AddVentureDialog implements AfterViewInit {

	public dialogRef = inject(MatDialogRef<AddVentureDialog>);
	public dialogContext: DialogContext<AddVentureDialogContextData> = inject(MAT_DIALOG_DATA);
	private func = inject(FuncService);

	public readonly form = new CommonForm<{ accVentureId: number }>(inject(Injector));
	public saveBtn = viewChild.required<ElementRef<HTMLButtonElement>>('saveBtn');

	// actually existingTeamName will always exist, as accTeamId is always defined for ventures in this dialog
	public availabilityCheck = linkedSignal<{ existingAccTeamName: string | undefined } | undefined>(() => {
		const pendingValue = this.ventureId().pendingValue();
		// When pendingValue changes, set availability to undefined
		return pendingValue != null ? undefined : undefined;
	});

	public buttonText = computed(() => this.availabilityCheck() ? 'Save' : 'Check');


	ngAfterViewInit() {
		this.form.setSubmitButton(this.saveBtn(), async (values: { accVentureId: number }, changed: string[]) => {

			if (changed.length === 0) return;

			const dialogContextData = this.dialogContext.data;
			if (!dialogContextData) return;

			if (!this.availabilityCheck()) {
				const result = await this.func.accTeam.isVentureAvailable({ accVentureId: values.accVentureId });
				this.availabilityCheck.set(result);
			} else {
				await this.func.accTeam.addVentureToTeam({
					accVentureId: values.accVentureId,
					accTeamId: dialogContextData.accTeamId,
				});
				this.availabilityCheck.set(undefined);
				this.dialogRef.close();
			}
		});
	}



	//
	// Form Fields
	//
	private readonly ventureOptions = computed<Option<number>[]>(() => {
		return this.dialogContext.data?.accVentures.reduce((a: Option<number>[], venture) => {
			a.push({
				value: venture.accVentureId,
				text: venture.ventureName,
			});
			return a;
		}, []) ?? [];
	});
	readonly ventureId = computed<CommonSelectField<number | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Venture',
			isRequired: true,
			options: this.ventureOptions(),
		}),
		formFieldName: 'accVentureId',
		form: this.form,
		actualValue: computed(() => undefined),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
	}));
}